import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import Swal from "sweetalert2";
import { API_BASE_URL, deleteRequest, getRequest, postRequest } from "../../../api";
import "./ParamInfosUsers.css";

/* ─── Constantes ─────────────────────────────────────────────────── */

const SECTIONS_FIELDS = {
    "Contact":                ["Email", "Téléphone", "Téléphone d'urgence", "Adresse", "Ville", "Code postal"],
    "Informations bancaires": ["RIB", "IBAN"],
    "Photo de profil":        ["Photo de profil"],
    "Photo de bannière":      ["Photo de bannière"],
};

const SECTION_STYLE = {
    "Contact":                { color: "#059669", bg: "#d1fae5" },
    "Informations bancaires": { color: "#7c3aed", bg: "#ede9fe" },
    "Photo de profil":        { color: "#d97706", bg: "#fef3c7" },
    "Photo de bannière":      { color: "#334785", bg: "#fde8e7" },
};

const MODE_CONFIG = {
    libre: { label: "Validation libre",    color: "#059669", bg: "#d1fae5" },
    admin: { label: "Validation admin",    color: "#d97706", bg: "#fef3c7" },
    delai: { label: "Avec délai",          color: "#7c3aed", bg: "#ede9fe" },
    notif: { label: "Notification seule",  color: "#204385", bg: "#e8edf7" },
};

const MODE_DESCRIPTIONS = {
    libre: "La modification est appliquée immédiatement, sans aucune validation requise.",
    admin: "La modification est mise en attente jusqu'à l'approbation d'un administrateur.",
    delai: "La modification est auto-approuvée après le délai configuré si l'admin n'agit pas.",
    notif: "La modification est appliquée immédiatement, l'admin est simplement notifié.",
};

const SECTION_KEYS = Object.keys(SECTIONS_FIELDS);

const FIELD_KEYS = {
    "Email": "email",
    "Téléphone": "phone",
    "Téléphone d'urgence": "phoneUrgence",
    "Adresse": "adresse",
    "Ville": "city",
    "Code postal": "codePostal",
    "RIB": "rib",
    "IBAN": "iban",
    "Photo de profil": "profilePictureUrl",
    "Photo de bannière": "bannerPictureUrl",
};

const FIELD_LABELS = Object.fromEntries(Object.entries(FIELD_KEYS).map(([label, key]) => [key, label]));

const getSectionForField = (fieldKey) => {
    const label = FIELD_LABELS[fieldKey];
    return SECTION_KEYS.find((section) => SECTIONS_FIELDS[section].includes(label)) || "Contact";
};

const emptyForm = {
    section:           "Contact",
    champ:             "Email",
    description:       "",
    mode:              "libre",
    delai:             24,
    active:            true,
    notifAdminModif:   true,
    notifAdminPending: false,
    notifUserConfirm:  true,
    notifUserStatus:   true,
};

/* ─── Composant ──────────────────────────────────────────────────── */

const ParamInfosUsers = () => {
    const user = useSelector((state) => state.auth?.user || state.user?.currentUser);
    const companyId = user?.companyId || sessionStorage.getItem("companyId") || sessionStorage.getItem("company_id");
    const token = sessionStorage.getItem("token") || localStorage.getItem("token");
    const [rules,         setRules]         = useState([]);
    const [q,             setQ]             = useState("");
    const [filterSection, setFilterSection] = useState("Tous");
    const [isModalOpen,   setIsModalOpen]   = useState(false);
    const [editing,       setEditing]       = useState(null);
    const [form,          setForm]          = useState(emptyForm);
    const [activeTab,     setActiveTab]     = useState("info");
    const [alertMsg,      setAlertMsg]      = useState(null);

    const toRule = (workflow) => ({
        id: workflow.id,
        section: getSectionForField(workflow.fieldKey),
        champ: FIELD_LABELS[workflow.fieldKey] || workflow.fieldKey,
        mode: String(workflow.validationMode || "LIBRE").toLowerCase(),
        delai: workflow.delay ?? null,
        active: workflow.isActive !== false,
        notifAdminModif: Boolean(workflow.notifAdminModif),
        notifAdminPending: Boolean(workflow.notifAdminPending),
        notifUserConfirm: Boolean(workflow.notifUserConfirm),
        notifUserStatus: Boolean(workflow.notifUserStatus),
        description: workflow.description || "",
    });

    useEffect(() => {
        if (!companyId) return;
        getRequest({
            url: `${API_BASE_URL}/api/user-field-workflows?companyId=${companyId}`,
            token,
        })
            .then((response) => setRules((response.data || []).map(toRule)))
            .catch(() => showAlert("Impossible de charger les règles de validation.", "error"));
    }, [companyId]);

    /* ── Filtrage ── */
    const filtered = useMemo(() => {
        const term = q.trim().toLowerCase();
        return rules.filter((r) => {
            const matchQ =
                !term ||
                (r.champ       || "").toLowerCase().includes(term) ||
                (r.section     || "").toLowerCase().includes(term) ||
                (r.description || "").toLowerCase().includes(term);
            const matchS = filterSection === "Tous" || r.section === filterSection;
            return matchQ && matchS;
        });
    }, [rules, q, filterSection]);

    /* ── Alert ── */
    const showAlert = (msg, type = "success") => {
        setAlertMsg({ msg, type });
        setTimeout(() => setAlertMsg(null), 2500);
    };

    /* ── Helpers modal ── */
    const openCreate = () => {
        setEditing(null);
        setForm(emptyForm);
        setActiveTab("info");
        setIsModalOpen(true);
    };

    const openEdit = (rule) => {
        setEditing(rule);
        setForm({ ...rule });
        setActiveTab("info");
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setEditing(null);
        setForm(emptyForm);
        setActiveTab("info");
    };

    /* ── onChange générique ── */
    const onChange = (e) => {
        const { name, value, type, checked } = e.target;
        const next = type === "checkbox" ? checked : value;

        setForm((prev) => {
            const updated = { ...prev, [name]: next };
            if (name === "section") {
                updated.champ = SECTIONS_FIELDS[value]?.[0] || "";
            }
            return updated;
        });
    };

    /* ── Soumission ── */
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!form.section || !form.champ) {
            Swal.fire({ icon: "warning", text: "La section et le champ sont obligatoires." });
            return;
        }

        if (!editing) {
            const exists = rules.find(
                (r) => r.section === form.section && r.champ === form.champ
            );
            if (exists) {
                Swal.fire({
                    icon: "warning",
                    text: `Une règle existe déjà pour "${form.champ}" dans "${form.section}".`,
                });
                return;
            }
        }

        if (!companyId) {
            showAlert("Société introuvable pour enregistrer la règle.", "error");
            return;
        }

        const payload = {
            ...form,
            delai: form.mode === "delai" ? Number(form.delai) : null,
        };

        try {
            const response = await postRequest({
                url: `${API_BASE_URL}/api/user-field-workflows`,
                token,
                data: {
                    id: editing?.id,
                    companyId: Number(companyId),
                    fieldKey: FIELD_KEYS[payload.champ],
                    validationMode: payload.mode.toUpperCase(),
                    delay: payload.delai,
                    isActive: payload.active,
                    notifAdminModif: payload.notifAdminModif,
                    notifAdminPending: payload.notifAdminPending,
                    notifUserConfirm: payload.notifUserConfirm,
                    notifUserStatus: payload.notifUserStatus,
                    description: payload.description,
                },
            });
            const savedRule = toRule(response.data);
            setRules((prev) => editing
                ? prev.map((rule) => rule.id === editing.id ? savedRule : rule)
                : [...prev, savedRule]);
            showAlert(editing ? "Règle mise à jour avec succès." : "Règle créée avec succès.");
        } catch {
            showAlert("Impossible d'enregistrer la règle.", "error");
            return;
        }

        closeModal();
    };

    /* ── Suppression ── */
    const handleDelete = (rule) => {
        Swal.fire({
            title: "Supprimer",
            text: `Supprimer la règle pour "${rule.champ}" ?`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Oui, supprimer",
            cancelButtonText: "Annuler",
            confirmButtonColor: "#334785",
        }).then((res) => {
            if (res.isConfirmed) {
                deleteRequest({
                    url: `${API_BASE_URL}/api/user-field-workflows/${rule.id}`,
                    token,
                })
                    .then(() => {
                        setRules((prev) => prev.filter((r) => r.id !== rule.id));
                        showAlert("Règle supprimée.");
                    })
                    .catch(() => showAlert("Impossible de supprimer la règle.", "error"));
            }
        });
    };

    /* ── Toggle actif ── */
    const handleToggleActive = async (rule) => {
        const updated = { ...rule, active: !rule.active };
        try {
            await postRequest({
                url: `${API_BASE_URL}/api/user-field-workflows`,
                token,
                data: {
                    id: updated.id,
                    companyId: Number(companyId),
                    fieldKey: FIELD_KEYS[updated.champ],
                    validationMode: updated.mode.toUpperCase(),
                    delay: updated.mode === "delai" ? updated.delai : null,
                    isActive: updated.active,
                    notifAdminModif: updated.notifAdminModif,
                    notifAdminPending: updated.notifAdminPending,
                    notifUserConfirm: updated.notifUserConfirm,
                    notifUserStatus: updated.notifUserStatus,
                    description: updated.description,
                },
            });
            setRules((prev) => prev.map((item) => item.id === rule.id ? updated : item));
        } catch {
            showAlert("Impossible de modifier le statut de la règle.", "error");
        }
    };

    /* ─── Render ─────────────────────────────────────────────────── */
    return (
        <div className="piv-page">

            {/* Header */}
            <div className="piv-header">
                <div>
                    <h2>Paramétrage des validations</h2>
                    <p className="piv-subtitle">
                        Définissez les règles d'approbation pour chaque champ modifiable par l'utilisateur
                    </p>
                </div>
                <button className="piv-btn piv-btn-primary" onClick={openCreate}>
                    + Ajouter une règle
                </button>
            </div>

            {/* Alert */}
            {alertMsg && (
                <div className={`piv-alert piv-alert-${alertMsg.type === "error" ? "danger" : "success"}`}>
                    {alertMsg.msg}
                </div>
            )}

            {/* Toolbar */}
            <div className="piv-toolbar">
                <input
                    className="piv-search"
                    placeholder="Rechercher par champ, section..."
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                />

                <div className="piv-filter-tabs">
                    {["Tous", ...SECTION_KEYS].map((sec) => (
                        <button
                            key={sec}
                            className={`piv-filter-tab ${filterSection === sec ? "active" : ""}`}
                            onClick={() => setFilterSection(sec)}
                        >
                            {sec}
                        </button>
                    ))}
                </div>

                <div className="piv-meta">
                    {filtered.length} règle{filtered.length !== 1 ? "s" : ""}
                </div>
            </div>

            {/* Table */}
            <div className="piv-table-wrapper">
                <table className="piv-table">
                    <thead>
                    <tr>
                        <th>Section</th>
                        <th>Champ</th>
                        <th>Mode de validation</th>
                        <th>Notif. admin</th>
                        <th>Délai</th>
                        <th>Statut</th>
                        <th style={{ width: 140, textAlign: "right" }}>Actions</th>
                    </tr>
                    </thead>
                    <tbody>
                    {filtered.map((rule) => {
                        const ss = SECTION_STYLE[rule.section] || {};
                        const ml = MODE_CONFIG[rule.mode]      || {};

                        return (
                            <tr key={rule.id}>
                                <td>
                                        <span className="piv-badge" style={{ color: ss.color, background: ss.bg }}>
                                            {rule.section}
                                        </span>
                                </td>

                                <td>
                                    <div className="piv-champ-cell">
                                        <div className="piv-champ-dot" />
                                        <span className="piv-champ-name">{rule.champ}</span>
                                    </div>
                                </td>

                                <td>
                                        <span className="piv-badge" style={{ color: ml.color, background: ml.bg }}>
                                            {ml.label}
                                        </span>
                                </td>

                                <td>
                                    {rule.notifAdminModif
                                        ? <span className="piv-notif-chip">✓ Oui</span>
                                        : <span className="piv-no">Non</span>
                                    }
                                </td>

                                <td className={rule.mode === "delai" ? "piv-delai-val" : "piv-delai-none"}>
                                    {rule.mode === "delai" && rule.delai ? `${rule.delai}h` : "—"}
                                </td>

                                <td>
                                    <button
                                        className={`piv-toggle ${rule.active ? "on" : "off"}`}
                                        onClick={() => handleToggleActive(rule)}
                                        title={rule.active ? "Désactiver" : "Activer"}
                                    >
                                        <span className="piv-toggle-knob" />
                                        <span className="piv-toggle-label">
                                                {rule.active ? "Actif" : "Inactif"}
                                            </span>
                                    </button>
                                </td>

                                <td className="piv-row-actions">
                                    <button className="piv-link-btn" onClick={() => openEdit(rule)}>
                                        Modifier
                                    </button>
                                    <button className="piv-link-btn danger" onClick={() => handleDelete(rule)}>
                                        Supprimer
                                    </button>
                                </td>
                            </tr>
                        );
                    })}

                    {filtered.length === 0 && (
                        <tr>
                            <td colSpan={7} className="piv-empty">
                                Aucune règle de validation trouvée
                            </td>
                        </tr>
                    )}
                    </tbody>
                </table>
            </div>

            {/* Modal */}
            {isModalOpen && (
                <div className="piv-overlay" onClick={closeModal}>
                    <div className="piv-modal" onClick={(e) => e.stopPropagation()}>

                        <div className="piv-modal-header">
                            <h3>
                                {editing ? "Modifier la règle de validation" : "Ajouter une règle de validation"}
                            </h3>
                            <button className="piv-modal-close" onClick={closeModal}>✕</button>
                        </div>

                        <div className="piv-tabs">
                            {[
                                { key: "info",  label: "Identification" },
                                { key: "valid", label: "Validation" },
                                { key: "notif", label: "Notifications" },
                            ].map(({ key, label }) => (
                                <button
                                    key={key}
                                    type="button"
                                    className={`piv-tab ${activeTab === key ? "active" : ""}`}
                                    onClick={() => setActiveTab(key)}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>

                        <form className="piv-form" onSubmit={handleSubmit}>

                            {/* ── Onglet Identification ── */}
                            {activeTab === "info" && (
                                <div className="piv-grid">
                                    <div className="piv-field">
                                        <label>Section *</label>
                                        <select
                                            name="section"
                                            value={form.section}
                                            onChange={onChange}
                                            disabled={!!editing}
                                        >
                                            {SECTION_KEYS.map((s) => (
                                                <option key={s} value={s}>{s}</option>
                                            ))}
                                        </select>
                                        {editing && (
                                            <span className="piv-hint">
                                                La section ne peut pas être modifiée après création.
                                            </span>
                                        )}
                                    </div>

                                    <div className="piv-field">
                                        <label>Champ *</label>
                                        <select
                                            name="champ"
                                            value={form.champ}
                                            onChange={onChange}
                                            disabled={!!editing}
                                        >
                                            {(SECTIONS_FIELDS[form.section] || []).map((f) => (
                                                <option key={f} value={f}>{f}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="piv-field piv-full">
                                        <label>Description</label>
                                        <textarea
                                            name="description"
                                            value={form.description}
                                            onChange={onChange}
                                            rows={3}
                                            placeholder="Décrivez la raison de cette règle de validation..."
                                        />
                                    </div>
                                </div>
                            )}

                            {/* ── Onglet Validation ── */}
                            {activeTab === "valid" && (
                                <div className="piv-grid">
                                    <div className="piv-field piv-full">
                                        <label>Mode de validation *</label>
                                        <div className="piv-modes-grid">
                                            {Object.entries(MODE_CONFIG).map(([key, cfg]) => (
                                                <label
                                                    key={key}
                                                    className={`piv-mode-card ${form.mode === key ? "selected" : ""}`}
                                                >
                                                    <input
                                                        type="radio"
                                                        name="mode"
                                                        value={key}
                                                        checked={form.mode === key}
                                                        onChange={onChange}
                                                    />
                                                    <div className="piv-mode-card-body">
                                                        <span className="piv-mode-card-title" style={{ color: cfg.color }}>
                                                            {cfg.label}
                                                        </span>
                                                        <span className="piv-mode-card-desc">
                                                            {MODE_DESCRIPTIONS[key]}
                                                        </span>
                                                    </div>
                                                </label>
                                            ))}
                                        </div>
                                    </div>

                                    {form.mode === "delai" && (
                                        <div className="piv-field">
                                            <label>Délai avant approbation auto (heures)</label>
                                            <input
                                                type="number"
                                                name="delai"
                                                value={form.delai}
                                                onChange={onChange}
                                                min={1}
                                                max={168}
                                                placeholder="Ex : 24"
                                            />
                                            <span className="piv-hint">
                                                Si l'admin n'agit pas dans ce délai, la modification est auto-approuvée.
                                            </span>
                                        </div>
                                    )}

                                    <div className="piv-field piv-full">
                                        <label className="piv-check-label">
                                            <input
                                                type="checkbox"
                                                name="active"
                                                checked={form.active}
                                                onChange={onChange}
                                            />
                                            Règle active
                                        </label>
                                    </div>
                                </div>
                            )}

                            {/* ── Onglet Notifications ── */}
                            {activeTab === "notif" && (
                                <div className="piv-grid">
                                    <div className="piv-field piv-full">
                                        <div className="piv-notif-section-title">Notifications administrateur</div>
                                    </div>
                                    <div className="piv-field piv-full">
                                        <label className="piv-check-label">
                                            <input type="checkbox" name="notifAdminModif" checked={form.notifAdminModif} onChange={onChange} />
                                            Notifier l'admin lors d'une modification
                                        </label>
                                    </div>
                                    <div className="piv-field piv-full">
                                        <label className="piv-check-label">
                                            <input type="checkbox" name="notifAdminPending" checked={form.notifAdminPending} onChange={onChange} />
                                            Rappel quotidien des demandes en attente
                                        </label>
                                    </div>

                                    <div className="piv-field piv-full">
                                        <div className="piv-notif-section-title">Notifications utilisateur</div>
                                    </div>
                                    <div className="piv-field piv-full">
                                        <label className="piv-check-label">
                                            <input type="checkbox" name="notifUserConfirm" checked={form.notifUserConfirm} onChange={onChange} />
                                            Notifier l'utilisateur de la réception de sa demande
                                        </label>
                                    </div>
                                    <div className="piv-field piv-full">
                                        <label className="piv-check-label">
                                            <input type="checkbox" name="notifUserStatus" checked={form.notifUserStatus} onChange={onChange} />
                                            Notifier l'utilisateur du statut (approuvé / refusé)
                                        </label>
                                    </div>
                                </div>
                            )}

                            <div className="piv-modal-actions">
                                <button type="button" className="piv-btn piv-btn-outline" onClick={closeModal}>
                                    Annuler
                                </button>
                                <button type="submit" className="piv-btn piv-btn-primary">
                                    {editing ? "Enregistrer" : "Créer la règle"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ParamInfosUsers;
